import { useMemo } from 'react';
import { Plus, Plane, FileCheck, Mail as MailIcon, CalendarDays } from '../../../../icons';
import {  } from '../../../../api/hr';
import { leaveAvailable } from '../../../../lib/leave-balance';
import { MyStatusAvatar } from '../../../../components/shared';
import { useAuthStore } from '../../../../store/authStore';
import { WidgetCard } from '../WidgetCard';
import { useWidgetActions } from '../widget-context';
import { useMenuGate } from '../../../../lib/menu-gate';
import { useLeaveSummary, useLeaves, useIncomingApprovals, useInbox, useMonthEvents, useMyProfile } from '../widget-queries';
// 휴가 길이는 분이 진실 — 서버가 준 문구를 그대로 쓰고, 옛 응답이면 일수로 되돌아간다 (2026-08-24).
import { leaveAmountText } from '../../../../lib/leave-amount';
// 2026-09-16 — 여기에 있던 이름표는 `lib/leave-type.ts` 의 것과 **글자 그대로 같았다**.
//   사본을 지우고 한 곳에서 가져온다(이름·차례는 그 파일의 검사가 잠근다).
//   S6 의 합친 코드(`half` · `quarter`)도 그 목록에 있어 여기서는 아무것도 더할 것이 없다.
import { LEAVE_TYPE_LABEL } from '../../../../lib/leave-type';
// S6 (2026-09-16) — 반차 · 반반차에 저장된 구간이 있으면 「14:00–16:00」을 붙인다(옛 기록은 없음).
import { leaveTimeRangeText } from '../../../../lib/partial-leave-time';

const STATUS_META: Record<string, { label: string; cls: string }> = {
  PENDING: { label: '승인대기', cls: 'dw-chip--wait' },
  APPROVED: { label: '승인', cls: 'dw-chip--ok' },
  REJECTED: { label: '반려', cls: 'dw-chip--reject' },
};

function mdRange(start: string, end: string): string {
  const fmt = (s: string) => {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };
  const a = fmt(start);
  const b = fmt(end);
  return a === b ? a : `${a} ~ ${b}`;
}

/** 휴가 위젯 (#2a) — 잔여 연차 · 신청 버튼 · 내 신청 내역. */
export function LeaveWidget() {
  const { openAttendanceRequest } = useWidgetActions();
  const summary = useLeaveSummary();
  const leaves = useLeaves();
  const loading = summary.loading || leaves.loading;
  const error = summary.error ?? leaves.error;
  const rows = leaves.data ?? [];

  const annualTotal = summary.data?.annual.total ?? null;
  const available = leaveAvailable(summary.data?.annual);
  const ringPct = annualTotal && annualTotal > 0 ? Math.max(0, Math.min(100, ((available ?? 0) / annualTotal) * 100)) : 0;
  /** 앞으로 남은 «승인된» 휴가 하나 — 가장 가까운 것. */
  const upcoming = useMemo(() => {
    const today = new Date(new Date().toDateString());
    return rows
      .filter((l) => l.status === 'APPROVED' && new Date(l.startDate) >= today)
      .sort((a, b) => (a.startDate < b.startDate ? -1 : 1))[0] ?? null;
  }, [rows]);

  return (
    <WidgetCard
      title="휴가"
      tail={<>잔여 <b className="dw-num">{leaveAvailable(summary.data?.annual) ?? 0}</b>일</>}
      loading={loading}
      error={error}
    >
      <div className="dw-stack">
        {/* ★★★ 시안 화면 L ② — 「얼마나 남았나」를 **고리와 세 숫자**로 먼저 말한다.
               ★ 총 연차가 `null` 이면 «모른다» 다(입사일도 부여 기록도 없다) — 0 도 15 도 아니라
                 고리를 안 그린다. 0 으로 그리면 「다 썼다」로 읽힌다. */}
        {annualTotal != null && (
          <div className="wi-lv__hero">
            <span className="wi-lv__ring" style={{ ['--p' as string]: `${ringPct}%` }} aria-hidden>
              <span className="wi-lv__ringin"><b>{available ?? 0}</b><span>잔여</span></span>
            </span>
            {/* 시안(axi-dashboard-v2)의 네 줄 — 「무엇이 남았나 · 얼마 썼나 · 무엇이 올라가 있나 · 얼마 받았나」.
                ★ 「결재 대기」는 **다시 세지 않는다** — 요약이 주는 `annual.pending` 을 그대로 쓴다
                  (두 곳이 따로 세면 언젠가 숫자가 갈린다). */}
            <span className="wi-lv__nums">
              <span className="wi-lv__num"><span>잔여</span><b>{available ?? 0}</b><i>일</i></span>
              <span className="wi-lv__num"><span>올해 쓴 연차</span><b>{summary.data?.annual.used ?? 0}</b><i>일</i></span>
              <span className="wi-lv__num"><span>결재 대기</span><b>{summary.data?.annual.pending ?? 0}</b><i>일</i></span>
              <span className="wi-lv__num"><span>{summary.data?.year ? `${summary.data.year} 부여` : '총'}</span><b>{annualTotal}</b><i>일</i></span>
            </span>
          </div>
        )}

        <button type="button" className="dw-btn dw-btn--primary dw-btn--block" onClick={() => openAttendanceRequest('leave')}>
          <Plus size={15} aria-hidden />휴가 신청
        </button>

        {/* ★ 「예정된 휴가」 — 승인된 앞날의 휴가. 없으면 절을 아예 안 세운다. */}
        {upcoming && (
          <div className="wi-lv__next">
            <Plane size={14} aria-hidden />
            <span>
              <b>{mdRange(upcoming.startDate, upcoming.endDate)}</b>
              {' '}{LEAVE_TYPE_LABEL[upcoming.type] ?? upcoming.type}
              {leaveTimeRangeText(upcoming) ? ` ${leaveTimeRangeText(upcoming)}` : ''} · {leaveAmountText(upcoming)} · <b>승인됨</b>
            </span>
          </div>
        )}

        <div className="dw-subhead"><span>내가 진행한 휴가</span></div>
        {rows.length === 0
          ? <div className="dw-card__state">신청 내역이 없습니다</div>
          : (
            <div className="dw-hist-list">
              {rows.slice(0, 3).map((l) => {
                const meta = STATUS_META[l.status] ?? { label: l.status, cls: '' };
                return (
                  <div key={l.id} className="dw-hist">
                    <span className={`dw-hist__bar ${meta.cls}`} aria-hidden />
                    <span className="dw-hist__main">
                      <span className="dw-hist__t">
                        {LEAVE_TYPE_LABEL[l.type] ?? l.type} · {mdRange(l.startDate, l.endDate)}
                        {leaveTimeRangeText(l) ? ` ${leaveTimeRangeText(l)}` : ''}
                      </span>
                      <span className="dw-hist__s">{leaveAmountText(l)}{l.reason ? ` · ${l.reason}` : ''}</span>
                    </span>
                    <span className={`dw-chip ${meta.cls}`}>{meta.label}</span>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </WidgetCard>
  );
}

/**
 * 내 프로필 위젯 (#2b · 시안 화면 L ① 「내 정보」).
 *
 * ★★★ 시안은 이 카드를 **오늘의 «내 할 일 창구»** 로 그렸다 — 이름·소속 아래에 「결재할 문서 4」
 *     처럼 **지금 나를 기다리는 것**이 줄줄이 선다. 종전에는 이름·메일·연차 셋뿐이라
 *     그 카드가 아무 일도 안 했다.
 * ★★★ **시안의 줄을 다 만들지는 않았다** — 「내 커뮤니티 새 글」·「참여할 설문」·「작성할 보고」는
 *     그 도메인이 아직 없다. **셀 수 없는 줄은 세우지 않는다**(0 으로 세우면 「없다」가 되어
 *     사실과 다르고, 눌러도 갈 곳이 없다).
 * ★ 끈 메뉴의 줄도 빠진다 — 못 가는 곳을 눌러 보게 하지 않는다.
 */
export function ProfileWidget() {
  const user = useAuthStore((s) => s.user);
  const { openSettings, openAttendanceRequest, go } = useWidgetActions();
  const summary = useLeaveSummary();
  const annual = summary.data?.annual;

  /*
   * 휴가 진행 막대 (2026-09-16) — 아래 세 숫자(잔여·사용·총)는 **그대로 두고** 비율만 더한다.
   *
   * ★★★ **채우는 것은 「잔여」다** (2026-09-16 사용자 결정 — 종전 「사용」에서 뒤집혔다).
   *     직원이 이 위젯에서 찾는 숫자는 「며칠 남았나」다. 그래서 막대가 차 있으면 **쓸 수 있는 날이
   *     많다**는 뜻이고, 줄어들수록 남은 것이 준다. 글자도 **「잔여 12.5 / 15일」** 로 적는다.
   * ★★★ `total` 은 **null 로 온다**(부여가 아직 안 잡힌 사람 · 무제한). 그때는 나눌 밑이 없으므로
   *      **막대를 아예 그리지 않는다** — 0/0 을 「0%」로 그리면 「연차가 하나도 없다」는 거짓말이 된다.
   *      **잔여를 모를 때(`null`)도 마찬가지다** — 0 으로 채워 「다 썼다」고 말하지 않는다.
   * ★★ **초과 사용(잔여가 음수)은 0% 에서 멈춘다.** 막대가 칸 밖으로 나가지 않게. 다만 **적히는
   *    숫자는 진짜 값**이다 — −3 을 0 으로 반올림하지 않는다(화면을 지키자고 값을 줄이면 거짓말이다).
   *    ⚠ 이때 **채울 것이 0** 이라 색 있는 부분이 아예 안 보인다. 그래서 초과는 **바탕(홈)**을
   *      물들여 알린다(`--over`) — 채움에 색을 주면 폭 0 이라 아무 말도 못 한다.
   * ★ 하나도 안 썼으면 **가득 찬다**(잔여 = 총). 종전 판에서는 빈 막대였다 — 뜻이 뒤집힌 자리다.
   */
  const leaveTotal = annual?.total ?? null;
  const leaveLeft = leaveAvailable(annual);
  const hasLeaveBar = typeof leaveTotal === 'number' && leaveTotal > 0 && leaveLeft != null;
  const leaveOver = hasLeaveBar && (leaveLeft as number) < 0;
  const leavePct = hasLeaveBar
    ? Math.min(100, Math.max(0, ((leaveLeft as number) / (leaveTotal as number)) * 100))
    : 0;

  /*
   * 소속·직급 (2026-09-16 · 시안의 「홍길동 <small>책임연구원</small> / 플랫폼개발팀」).
   *
   * ★★★ **없으면 그 줄을 안 그린다** — 부서가 없는 사람(미배치 · 대표)과 직급이 없는 사람이
   *     실제로 있다. 빈 칸이나 「—」로 채우면 **자리가 비어 있는 것처럼** 보이고, 「소속 없음」
   *     이라고 적으면 **없다고 단정**하는 것이 된다. 그냥 안 그린다.
   * ★★ 이 카드의 로딩·오류는 **종전대로 연차 요약만** 본다. 소속을 못 불러왔다고 카드 전체가
   *    「로딩…」이 되면, 지금 잘 보이던 것까지 사라진다.
   * ★ 부서·팀이 둘 다 있으면 「본부 · 팀」으로 잇는다.
   */
  const profile = useMyProfile();
  const position = profile.data?.position ?? null;
  const org = [profile.data?.org?.divisionName, profile.data?.org?.teamName]
    .filter((s): s is string => !!s && s.trim() !== '')
    .join(' · ') || null;

  const apprGate = useMenuGate('approval', '전자결재');
  const mailGate = useMenuGate('mail', '메일');
  const calGate = useMenuGate('calendar', '캘린더');

  const approvals = useIncomingApprovals(apprGate.enabled);
  const inbox = useInbox(mailGate.enabled);
  const now = new Date();
  const events = useMonthEvents(now.getFullYear(), now.getMonth(), calGate.enabled);

  const today = useMemo(() => {
    const rows = (events.data ?? []).filter((e) => {
      const t = new Date(e.startAt);
      return t.getFullYear() === now.getFullYear() && t.getMonth() === now.getMonth() && t.getDate() === now.getDate();
    });
    const next = rows.find((e) => new Date(e.startAt).getTime() >= Date.now()) ?? rows[0] ?? null;
    return { count: rows.length, next };
  }, [events.data]);

  const unread = (inbox.data ?? []).filter((m) => !m.isRead).length;

  const rows: Array<{ key: string; icon: typeof FileCheck; label: string; n: string; tone?: 'on' | 'warn'; to: string }> = [];
  if (apprGate.enabled) {
    rows.push({
      key: 'appr', icon: FileCheck, label: '결재할 문서',
      n: approvals.data ? String(approvals.data.length) : '—',
      tone: (approvals.data?.length ?? 0) > 0 ? 'on' : undefined, to: '/approval/pending',
    });
  }
  if (mailGate.enabled) {
    rows.push({
      key: 'mail', icon: MailIcon, label: '안 읽은 메일',
      n: inbox.data ? String(unread) : '—',
      tone: unread > 0 ? 'warn' : undefined, to: '/mail',
    });
  }
  rows.push({
    key: 'leave', icon: Plane, label: '내 잔여 연차',
    n: leaveAvailable(annual) != null ? `${leaveAvailable(annual)}일` : '—',
    to: '/erp/attendance/me',
  });

  return (
    <WidgetCard
      title="내 프로필"
      link={{ label: '설정', onClick: openSettings }}
      loading={summary.loading}
      error={summary.error}
    >
      <div className="dw-prof">
        {/* 점 색 = 상태바에서 고른 근무 상태(2026-08-12). 미설정이면 종전 초록 그대로. */}
        <MyStatusAvatar userId={user?.id ?? 0} name={user?.name ?? '사용자'} src={user?.avatarUrl} size="lg" />
        <div className="dw-prof__name">
          {user?.name ?? '사용자'}
          {position && <small className="dw-prof__pos">{position}</small>}
        </div>
        {org && <div className="dw-prof__org">{org}</div>}
        <div className="dw-prof__meta">{user?.email ?? ''}</div>
        {/* ★ 「오늘 무엇이 있나」 — 시안이 이름 바로 아래에 둔 한 줄. */}
        {calGate.enabled && today.count > 0 && (
          <button type="button" className="wi-me__today" onClick={() => go('/calendar')}>
            <CalendarDays size={12} aria-hidden />
            오늘 일정 <b>{today.count}건</b>
            {today.next && <> · 다음 <b>{new Date(today.next.startAt).toTimeString().slice(0, 5)} {today.next.title}</b></>}
          </button>
        )}
      </div>

      <div className="dw-divider" />

      <div className="wi-me__list">
        {rows.map((r) => (
          <button type="button" key={r.key} className="wi-row" onClick={() => go(r.to)}>
            <span className="wi-row__i"><r.icon size={14} aria-hidden /></span>
            <span className="wi-row__t">{r.label}</span>
            <span className={`wi-row__n${r.tone ? ` wi-row__n--${r.tone}` : ''}`}>{r.n}</span>
          </button>
        ))}
      </div>

      <div className="dw-stack">
        <div className="dw-subhead">
          <span>휴가 현황{hasLeaveBar && <span className="dw-subhead__s"> · 잔여 {leaveLeft} / {leaveTotal}일</span>}</span>
          <button type="button" className="dw-btn dw-btn--primary dw-btn--xs" onClick={() => openAttendanceRequest('leave')}>신청</button>
        </div>
        {hasLeaveBar && (
          <div
            className={`dw-lvbar${leaveOver ? ' dw-lvbar--over' : ''}`}
            role="progressbar"
            aria-label="잔여 연차"
            aria-valuemin={0}
            aria-valuemax={leaveTotal}
            aria-valuenow={Math.min(leaveTotal as number, Math.max(0, leaveLeft as number))}
            aria-valuetext={`총 ${leaveTotal}일 중 ${leaveLeft}일 남음`}
          >
            <span className="dw-lvbar__f" style={{ width: `${leavePct}%` }} />
          </div>
        )}
        <div className="dw-stat3">
          <div><div className="dw-stat3__k">잔여</div><div className="dw-stat3__v">{leaveAvailable(annual) ?? 0}</div></div>
          <div><div className="dw-stat3__k">사용</div><div className="dw-stat3__v dw-stat3__v--muted">{annual?.used ?? 0}</div></div>
          <div><div className="dw-stat3__k">총</div><div className="dw-stat3__v dw-stat3__v--muted">{annual?.total ?? 0}</div></div>
        </div>
      </div>
    </WidgetCard>
  );
}
